import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import type { AdminClient } from "../api.js";
import type { BalanceAccount, BalanceHistory, BalancesSnapshot, LedgerAccount } from "../console/types.js";
import { clear, h, safeText } from "../dom.js";
import { renderCheckNow } from "../ui/check-now.js";
import { disclosure } from "../ui/components.js";
import { dateText, timeAgoText } from "../ui/display-words.js";
import { bankConnection, cashFigure, readerRole } from "../ui/facts.js";
import { failureCopy } from "../ui/failure.js";
import { NO_COMPANY_NAMES } from "../ui/display-words.js";
import { account as accountFace } from "../ui/face.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import { decimalToCents, formatAsOf, formatCents } from "../ui/money.js";
import { lastHealth } from "../ui/nav-bus.js";
import { defaultPeriod } from "../ui/period-control.js";
import type { CompanyHealth, CompanySlug, StaffRole } from "../types.js";
import { runAnnouncedAction } from "../ui/announced-action.js";
import { navLabel, sentence } from "../ui/sentences.js";
import {
  ACCOUNT_CONNECTED,
  BankInvitePossiblySent,
  BankWaitTimeout,
  BANK_WAIT_TIMEOUT_COPY,
  connectBankAccount,
  startAgainControl,
} from "./connect-account.js";

export interface BalancesPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  /** The role the shell already knows (decision 10: unknown reads as the AM). */
  role?: StaffRole;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /** The shell's last fleet-health read failed (the reading is the last one). */
  statusReadFailed?: () => boolean;
  /**
   * The shell's in-flight fleet-health read, so the reconnect block waits for
   * the reading it is about to speak for instead of saying it could not check
   * (round 2, 2).
   */
  healthReadSettled?: () => Promise<void> | undefined;
  /**
   * Ask the shell for a fresh fleet-health read (an explicit person action:
   * Retry, or a finished repair). Resolves when that read has settled.
   */
  readHealth?: () => Promise<void>;
  /**
   * Be told whenever the shell's fleet-health read settles, so this block
   * repaints with the reading a Refresh asked for instead of the old one.
   * Returns an unsubscribe.
   */
  onHealthSettled?: (listener: () => void) => () => void;
}

/** One bank connection that needs a sign-in, as the health read describes it. */
type BrokenConnection = CompanyHealth["plaidItems"][number];

/**
 * The connected items of one company's health reading.
 *
 * B2b-1: "is a bank connected" is `bankConnection`. This page reads the item
 * LIST for two things the fact cannot answer — which bank is broken (so the
 * owner's Reconnect names it) and whether a connected bank simply has no
 * reading yet. One function keeps that read visible to the facts guard as one
 * recorded "correct use" site.
 */
function plaidItemsOf(
  health: CompanyHealth | null | undefined,
): readonly BrokenConnection[] {
  return health?.plaidItems ?? [];
}

/**
 * Accounts — every bank account, big current balance; whole row expands
 * inline to the daily history timeline (docs/design/console-ui-design.md §2).
 */
export async function renderBalancesPage(opts: BalancesPageOptions): Promise<void> {
  const { client, company, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);

  let loadToken = 0;
  let loadedAt = new Date();

  root.append(h("h1", { class: "page-title" }, [navLabel("balances")]));
  root.append(
    h("p", { class: "lede" }, [
      "Connected bank accounts. Tap a row for daily history.",
    ]),
  );

  const connectBanner = h("div", {
    class: "banner",
    role: "status",
    "data-role": "connect-banner",
    hidden: "true",
  }, []);
  const connectBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "connect-account",
      "aria-label": "Connect a bank account",
    },
    ["Connect account"],
  );
  const connectStatus = h("p", {
    class: "muted caption",
    "data-role": "connect-status",
    hidden: "true",
  });
  const role: StaffRole = readerRole(opts.role);
  const ownerWrites = role === "owner";
  if (ownerWrites) {
    root.append(
      h("div", { class: "form-row", "data-role": "connect-account" }, [
        connectBtn,
        connectStatus,
      ]),
    );
    root.append(connectBanner);
  }

  const freshnessHost = h("div", { "data-role": "freshness-host" });
  root.append(freshnessHost);

  // Check now asks Moov and Plaid for this company right now. It is separate
  // from Refresh above, which only re-reads our own database.
  const checkNowHost = h("div", { "data-role": "check-now-host" });
  root.append(checkNowHost);
  renderCheckNow({
    client,
    company,
    root: checkNowHost,
    stillCurrent,
    ...(opts.role !== undefined ? { role: opts.role } : {}),
  });

  const banner = h("div", {
    class: "banner banner-err",
    role: "alert",
    "data-role": "balances-error",
    hidden: "true",
  }, []);
  root.append(banner);

  // C-10: a bank that needs a sign-in is said HERE, where the Reconnect action
  // is, instead of this page showing the same bank as if it were healthy. The
  // block is painted from the shell's own health read; a failed read says it
  // could not check rather than implying the connections are fine.
  const reconnectHost = h("div", { "data-role": "bank-reconnect-host" });
  root.append(reconnectHost);
  // Round 3 (1): the wait for the bank (up to thirty minutes) and its failure
  // are what the person is watching for, so both are on screen where the
  // Reconnect control is. They sit OUTSIDE the block: a repair removes the
  // block, and the line that said what was happening must not go with it.
  // Deliberately not live regions: the hidden one below already announces
  // every one of these sentences, and a second announcer would say each twice.
  const reconnectStatus = h("p", {
    class: "muted caption",
    "data-role": "bank-reconnect-status",
    hidden: "true",
  });
  const reconnectError = h("div", {
    class: "banner banner-err",
    "data-role": "bank-reconnect-error",
    hidden: "true",
  });
  root.append(reconnectStatus, reconnectError);
  // Outside the block on purpose: a successful repair removes the block, and
  // the announcement of it must outlive that repaint (round 2, 1).
  const reconnectLive = h("div", {
    class: "aria-live",
    "aria-live": "polite",
    "data-role": "bank-reconnect-live",
  });
  root.append(reconnectLive);

  const content = h("div", { "data-role": "balances-content" });
  root.append(content);

  function paintFreshness(): void {
    clear(freshnessHost);
    freshnessHost.append(
      createFreshnessCaption({
        updatedAt: loadedAt,
        onRefresh: () => {
          loadInteractive();
        },
      }),
    );
  }

  function hideBanner(): void {
    clear(banner);
    banner.hidden = true;
    banner.setAttribute("hidden", "true");
  }

  /** The visible line for a Reconnect in progress (round 3, 1). */
  function setReconnectStatus(message: string | null): void {
    clear(reconnectStatus);
    if (message === null || message.length === 0) {
      reconnectStatus.hidden = true;
      reconnectStatus.setAttribute("hidden", "true");
      return;
    }
    reconnectStatus.hidden = false;
    reconnectStatus.removeAttribute("hidden");
    reconnectStatus.append(document.createTextNode(message));
  }

  /**
   * The visible failure of a Reconnect (round 3, 1).
   *
   * round-11 item 24: the read-failure table decides the words; the server's own
   * message may only travel in a "Show details" disclosure. The wait's own
   * timeout keeps the guidance it always had (round 3 item 4: the bank may have
   * connected just after we stopped watching, so it is not a failed save).
   */
  function showReconnectFailure(error: unknown): void {
    clear(reconnectError);
    reconnectError.hidden = false;
    reconnectError.removeAttribute("hidden");
    const copy = connectFailure(error);
    reconnectError.append(document.createTextNode(copy.sentence));
    if (copy.detail !== null) {
      reconnectError.append(disclosure("Show details", h("p", {}, [copy.detail])));
    }
    // Round 2 review, M3: a lost invite response blocks a second mint, so the
    // failure is not a dead end — "Start again" clears the mark.
    if (error instanceof BankInvitePossiblySent) {
      reconnectError.append(document.createTextNode(" "), startAgainControl(company, hideReconnectError));
    }
  }

  /**
   * The words for a failed connect or reconnect, from the one failure table.
   * A wait that timed out is our own sentence, not the server's.
   */
  function connectFailure(error: unknown): { sentence: string; detail: string | null } {
    if (error instanceof BankWaitTimeout) {
      return { sentence: `Reconnect failed: ${BANK_WAIT_TIMEOUT_COPY}`, detail: null };
    }
    const copy = failureCopy(error, { role, context: "write" });
    return { sentence: copy.sentence, detail: copy.detail };
  }

  function hideReconnectError(): void {
    clear(reconnectError);
    reconnectError.hidden = true;
    reconnectError.setAttribute("hidden", "true");
  }

  function showBannerError(): void {
    clear(banner);
    banner.hidden = false;
    banner.removeAttribute("hidden");
    const retry = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "retry", "aria-label": "Retry loading balances" },
      ["Retry"],
    );
    retry.addEventListener("click", () => {
      retryLoad();
    });
    banner.append(
      h("strong", {}, ["Balances could not be loaded."]),
      h("p", {}, ["Try again."]),
      retry,
    );
  }

  function renderAccountRow(account: BalanceAccount, books: readonly LedgerAccount[] | null, depositoryCount: number): HTMLElement {
    const maskDisplay = `••••${amVisibleDisplayText(account.mask)}`;
    // The bank's own account name is untrusted text: a bidi override could
    // reverse it. Shown (and put in attributes) hazard-stripped; the raw name is
    // kept only for the history request, where it is the key.
    const shownName = amVisibleDisplayText(account.name);
    const timeline = h("div", {
      class: "timeline",
      "data-role": "balance-timeline",
      hidden: "true",
    });

    const row = h(
      "button",
      {
        type: "button",
        class: "account-row",
        role: "listitem",
        "data-account": shownName,
        "data-action": "view-history",
        "aria-label": `View balance history for ${shownName}`,
        "aria-expanded": "false",
      },
      [
        h("div", { class: "account-row-head" }, [
          h("div", {}, [
            h("h2", {}, [safeText(shownName)]),
            h("p", { class: "muted caption" }, [
              safeText(accountCaption(account, maskDisplay)),
            ]),
          ]),
          h("p", { class: "account-balance" }, [formatAmount(account.currentBalance)]),
        ]),
        booksNote(account, books, depositoryCount),
        timeline,
      ],
    );

    let open = false;
    row.addEventListener("click", (event) => {
      // Don't toggle when clicking inside an already-open timeline control.
      if ((event.target as HTMLElement).closest(".timeline button")) return;
      if (open) {
        open = false;
        timeline.hidden = true;
        timeline.setAttribute("hidden", "true");
        row.setAttribute("aria-expanded", "false");
        return;
      }
      open = true;
      row.setAttribute("aria-expanded", "true");
      void loadHistory(account.name, timeline);
    });

    return row;
  }

  async function loadHistory(accountName: string, timeline: HTMLElement): Promise<void> {
    clear(timeline);
    timeline.hidden = false;
    timeline.removeAttribute("hidden");
    timeline.append(h("p", { class: "muted" }, ["Loading history…"]));
    try {
      const history: BalanceHistory = await client.getBalanceHistory(company, accountName);
      if (!stillCurrent()) return;
      clear(timeline);
      if (history.points.length === 0) {
        timeline.append(h("p", { class: "empty" }, ["No history captured yet."]));
      } else {
        const list = h("ol", { class: "timeline-list" });
        for (const point of history.points) {
          const when = dateText(point.asOf);
          list.append(
            h("li", { class: "timeline-point" }, [
              h("span", { class: "timeline-date" }, [when === "" ? "Unknown date" : when]),
              h("span", { class: "timeline-value" }, [formatAmount(point.currentBalance)]),
            ]),
          );
        }
        timeline.append(list);
      }
    } catch (error) {
      clear(timeline);
      // round-11 item 24: the face reads the decided read failure; the server's
      // own words (scrubbed) travel in the disclosure and nowhere else.
      const copy = failureCopy(error, { role, context: "read" });
      timeline.append(
        h("p", { class: "banner banner-err", role: "alert" }, [
          copy.sentence,
          ...(copy.detail === null
            ? []
            : [disclosure("Show details", h("p", {}, [copy.detail]))]),
        ]),
      );
    }
  }

  let booksAccounts: readonly LedgerAccount[] | null = null;

  /**
   * What the empty state can honestly say. A snapshot with no accounts is not
   * the same as no bank: a stored item is connected and its balances arrive
   * with the first check (rd R11 R6-12). If the shell has no health reading
   * yet, say only what waits on the check.
   */
  /**
   * The shell's reading for this company, or undefined when it does not mention
   * it. `bankConnection(undefined)` is "no reading yet", never "no bank": a
   * reading that omitted this company proves nothing about its bank.
   */
  function bankRow(): CompanyHealth | null {
    const reading = lastHealth();
    if (reading === null) return null;
    return reading.companies.find((entry) => entry.company === company) ?? null;
  }

  function bankForCompany(): ReturnType<typeof bankConnection> {
    return bankConnection(bankRow());
  }

  function emptyStateCopy(): string {
    const bank = bankForCompany();
    if (bank === "none") return sentence("no_bank_connected", role);
    if (bank === "needs_reconnect") return "Balances appear after the bank is reconnected.";
    if (bank === "healthy") {
      return "Connected. Balances appear after the first check. Press Check now.";
    }
    // "No reading yet" is not "no bank" (rd-2 MEDIUM 4): a stored healthy bank
    // that has never synced is connected, and its balances arrive with the
    // first check (rd R11 R6-12).
    if (plaidItemsOf(bankRow()).length > 0) {
      return "Connected. Balances appear after the first check. Press Check now.";
    }
    // No reading at all: say only what is always true. "Balances appear after
    // the first check" claimed a check was still to come, next to a Check now
    // line that could say it ran six minutes ago (rd r13 L3.7).
    return "No balances to show yet.";
  }

  /**
   * rc-4 B5: a snapshot can hold rows and still be a company with no bank at
   * all (a Moov wallet arrives as its own depository account). The rows stay,
   * and the page says which fact it is: `cashFigure` decides, so this line and
   * Home's cash tile cannot disagree.
   */
  function bankStateCopy(snapshot: BalancesSnapshot): string | null {
    const figure = cashFigure(snapshot, bankForCompany());
    if (figure.kind === "no_accounts") return sentence("no_bank_connected", role);
    return null;
  }

  function renderContent(snapshot: BalancesSnapshot): void {
    clear(content);
    const bankState = bankStateCopy(snapshot);
    const empty = snapshot.asOf === null || snapshot.accounts.length === 0;
    const emptyCopy = empty ? emptyStateCopy() : null;
    // With no bank and no rows both lines say "No bank connected yet.": the
    // page says it once (rd r12 F1, the click-through's no-bank page).
    if (bankState !== null && bankState !== emptyCopy) {
      content.append(
        h("p", { class: "muted", "data-role": "balances-bank-state" }, [bankState]),
      );
    }
    if (snapshot.asOf === null || snapshot.accounts.length === 0) {
      content.append(
        h("p", { class: "empty", "data-role": "balances-empty" }, [emptyCopy ?? emptyStateCopy()]),
      );
      return;
    }
    const checked = snapshot.asOf.includes("T")
      ? timeAgoText(snapshot.asOf)
      : formatAsOf(snapshot.asOf);
    content.append(
      h("p", { class: "muted caption" }, [
        checked === "" ? "Last checked some time ago" : `Last checked ${checked}`,
      ]),
    );
    const depositoryCount = snapshot.accounts.filter((account) => account.type === "depository").length;
    const list = h("div", { class: "balances-list", role: "list" });
    for (const account of snapshot.accounts) {
      list.append(renderAccountRow(account, booksAccounts, depositoryCount));
    }
    content.append(list);
  }

  async function loadInner(): Promise<void> {
    const token = ++loadToken;
    hideBanner();
    clear(content);
    content.append(h("p", { class: "muted", "data-role": "balances-loading" }, ["Loading accounts…"]));
    try {
      const [snapshot, books] = await Promise.all([
        client.getBalances(company),
        loadBooksCash(client, company),
      ]);
      if (token !== loadToken || !stillCurrent()) return;
      loadedAt = new Date();
      booksAccounts = books;
      paintFreshness();
      renderContent(snapshot);
    } catch {
      if (token !== loadToken || !stillCurrent()) return;
      clear(content);
      showBannerError();
    }
  }

  function loadInteractive(): void {
    void loadInner();
    if (opts.onHealthSettled !== undefined) {
      // The Refresh click makes the shell take a new reading; until it lands,
      // say we are checking rather than repeat the reading it is replacing
      // (round 2, 2). The subscription below paints the result.
      paintChecking();
      return;
    }
    void paintReconnect();
  }

  /** Retry (an explicit person action): re-read the health before repainting. */
  function retryLoad(): void {
    void loadInner();
    void recheckConnections();
  }

  async function recheckConnections(): Promise<void> {
    await opts.readHealth?.();
    if (!stillCurrent()) return;
    await paintReconnect();
  }

  /**
   * The shell's fleet-health reading, once the read it is waiting for (if one
   * is in flight) has settled. No read of its own: the shell already reads
   * fleet health on every navigation, and the route is polled by the bank-link
   * wait, so it is deliberately not cached.
   *
   * Round 2 (2): this used to give up after a second and say "We could not
   * check the bank connections just now" for a company that was fine. While the
   * read is still in flight the block says it is checking, and it repaints when
   * the reading lands.
   */
  async function settledReading(): Promise<CompanyHealth[] | null> {
    const ready = lastHealth();
    if (ready !== null) return ready.companies;
    const pending = opts.healthReadSettled?.();
    if (pending === undefined) {
      // Rendered on its own, with no shell to wait for: give the read a
      // moment to land, then say it could not be checked.
      return await pollReading();
    }
    paintChecking();
    await pending;
    return lastHealth()?.companies ?? null;
  }

  async function pollReading(): Promise<CompanyHealth[] | null> {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const snapshot = lastHealth();
      if (snapshot !== null) return snapshot.companies;
      if (!stillCurrent()) return null;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return null;
  }

  function paintChecking(): void {
    clear(reconnectHost);
    reconnectHost.hidden = false;
    reconnectHost.removeAttribute("hidden");
    reconnectHost.append(
      h("p", {
        class: "muted",
        role: "status",
        "data-role": "bank-reconnect",
      }, ["Checking the bank connections…"]),
    );
  }

  /**
   * C-10: which bank connections need a person, and the one action that fixes
   * them. The health reading is the only place the console knows a
   * connection's state, so no reading — or a reading the shell says failed —
   * is said as it is, instead of leaving this page implying every connection
   * is fine.
   */
  async function paintReconnect(fresh?: readonly CompanyHealth[]): Promise<void> {
    let companies = fresh ?? lastHealth()?.companies ?? null;
    if (companies === null && fresh === undefined) {
      companies = await settledReading();
    }
    if (!stillCurrent()) return;
    composeReconnect(companies);
  }

  function composeReconnect(companies: readonly CompanyHealth[] | null): void {
    clear(reconnectHost);
    if (companies === null) {
      reconnectHost.hidden = false;
      reconnectHost.removeAttribute("hidden");
      reconnectHost.append(
        h("p", {
          class: "muted",
          role: "status",
          "data-role": "bank-reconnect",
        }, ["We could not check the bank connections just now."]),
      );
      return;
    }
    const row = companies.find((entry) => entry.company === company);
    // The ONE place this page reads the item list: the block names the bank that
    // needs a person and offers the owner its Reconnect control. "Is there a
    // bank" and "what is cash" come from `bankConnection` / `cashFigure`.
    const broken = plaidItemsOf(row).filter((item) => item.status !== "healthy");
    if (broken.length === 0) {
      reconnectHost.hidden = true;
      reconnectHost.setAttribute("hidden", "true");
      return;
    }
    const stale = opts.statusReadFailed?.() ?? false;
    reconnectHost.hidden = false;
    reconnectHost.removeAttribute("hidden");
    const section = h("section", {
      class: "banner banner-warn",
      role: "status",
      "data-role": "bank-reconnect",
    });
    for (const item of broken) section.append(reconnectLine(item, stale));
    if (!ownerWrites) {
      // Issuing a bank invitation is the owner's, so the account manager is
      // told who can, once for the company (round 2, 8), and given no control
      // that would fail.
      section.append(
        h("p", { "data-role": "bank-reconnect-ask" }, [
          sentence("bank_needs_reconnect", role),
        ]),
      );
    }
    reconnectHost.append(section);
  }

  /** One connection, in the same state words Connection status uses (round 2, 4). */
  function reconnectLine(item: BrokenConnection, stale: boolean): HTMLElement {
    const name = safeText(amVisibleDisplayText(item.institution ?? ""), "Bank");
    // round-11 item 26: the decided word is "Needs reconnecting." — a sign-in
    // IS the reconnect, and every non-healthy item needs the same action, so
    // there is no third spelling for a revoked or broken bank.
    const state = sentence("bank_needs_reconnect", "owner");
    // The shared stale form (rd-z-fixes-background §3): "We could not check
    // just now. Last known: needs reconnecting."
    const words = stale
      ? `We could not check just now. Last known: ${state.charAt(0).toLowerCase()}${state.slice(1)}`
      : state;
    const line = h("p", { "data-role": "bank-reconnect-line" }, [`${name}: ${words}`]);
    if (!ownerWrites) return line;
    const reconnect = h(
      "button",
      {
        type: "button",
        class: "btn btn-secondary",
        "data-action": "reconnect-bank",
        "aria-label": `Reconnect ${name}`,
      },
      ["Reconnect"],
    );
    reconnect.addEventListener("click", () => {
      hideReconnectError();
      setReconnectStatus("Preparing connection…");
      // A blocked popup's URL is the only way to finish the flow, so it stays
      // on screen while the wait's progress lines go by (round 3, 1).
      let blockedUrl: string | null = null;
      void runAnnouncedAction(reconnectLive, stillCurrent, "Reconnect", async () => {
        reconnect.disabled = true;
        try {
          // The same invitation flow Connect account uses; nothing new.
          await connectBankAccount({
            client,
            company,
            expectedItemId: item.plaidItemId,
            expectedInstitution: item.institution ?? null,
            isCurrent: stillCurrent,
            scope: reconnectHost,
            onStatus: (message) => {
              if (!stillCurrent()) return;
              // A blocked popup's URL and the long wait are the person's only
              // way to finish, so they are on screen as well as announced.
              reconnectLive.textContent = message;
              if (message.startsWith("Popup blocked.")) blockedUrl = message;
              setReconnectStatus(blockedUrl ?? message);
            },
          });
          if (!stillCurrent()) return;
          // The repair makes the block go, so the live region cannot be the
          // only place the person reads it (round 2, 1).
          reconnectLive.textContent = ACCOUNT_CONNECTED;
          setReconnectStatus(null);
          showConnectBanner(true, ACCOUNT_CONNECTED);
          // The shell's reading (and with it the banner) is older than the one
          // this flow just saw: re-read so all three surfaces agree.
          await opts.readHealth?.();
          if (!stillCurrent()) return;
          await paintReconnect();
          await loadInner();
        } catch (error) {
          // Round 3 (1): an abandoned or failed flow used to re-enable the
          // button silently. Say what happened where the person is looking.
          if (stillCurrent()) {
            setReconnectStatus(null);
            showReconnectFailure(error);
          }
          // The blocked invitation already says its own sentence in the row,
          // and the shared announcer would add "That did not save." beside it.
          if (error instanceof BankInvitePossiblySent) return;
          throw error;
        } finally {
          if (stillCurrent()) reconnect.disabled = false;
        }
      });
    });
    line.append(document.createTextNode(" "), reconnect);
    return line;
  }

  function hideConnectBanner(): void {
    clear(connectBanner);
    connectBanner.hidden = true;
    connectBanner.setAttribute("hidden", "true");
    connectBanner.className = "banner";
  }

  function showConnectBanner(
    ok: boolean,
    message: string,
    detail: string | null = null,
  ): void {
    clear(connectBanner);
    connectBanner.hidden = false;
    connectBanner.removeAttribute("hidden");
    connectBanner.className = ok ? "banner banner-ok" : "banner banner-err";
    connectBanner.append(document.createTextNode(message));
    if (detail !== null) {
      connectBanner.append(disclosure("Show details", h("p", {}, [detail])));
    }
  }

  function setConnectStatus(message: string | null): void {
    clear(connectStatus);
    if (message === null || message.length === 0) {
      connectStatus.hidden = true;
      connectStatus.setAttribute("hidden", "true");
      return;
    }
    connectStatus.hidden = false;
    connectStatus.removeAttribute("hidden");
    connectStatus.append(document.createTextNode(message));
  }

  if (ownerWrites) connectBtn.addEventListener("click", () => {
    void (async () => {
      connectBtn.disabled = true;
      connectBtn.setAttribute("disabled", "true");
      hideConnectBanner();
      setConnectStatus("Preparing connection…");
      try {
        await connectBankAccount({
          client,
          company,
          isCurrent: stillCurrent,
          scope: connectBtn.parentElement ?? root,
          onStatus: (message) => {
            if (!stillCurrent()) return;
            setConnectStatus(message);
          },
        });
        if (!stillCurrent()) return;
        showConnectBanner(true, ACCOUNT_CONNECTED);
        setConnectStatus(null);
        // The new connection must show in the reconnect block too, and the
        // shell's own reading is older than it.
        await opts.readHealth?.();
        if (!stillCurrent()) return;
        await paintReconnect();
        await loadInner();
      } catch (error) {
        if (!stillCurrent()) return;
        // round-11 item 24: the decided failure sentence on the face, the
        // server's own words behind "Show details".
        const copy = connectFailure(error);
        showConnectBanner(false, copy.sentence, copy.detail);
        setConnectStatus(null);
        // Round 2 review, M3: the way out of a blocked invitation.
        if (error instanceof BankInvitePossiblySent) {
          connectBanner.append(document.createTextNode(" "), startAgainControl(company));
        }
      } finally {
        if (stillCurrent()) {
          connectBtn.disabled = false;
          connectBtn.removeAttribute("disabled");
        }
      }
    })();
  });

  // Round 2 (2): the shell tells every page that speaks for its health reading
  // when a read settles, so a Refresh (which starts a read on the same click)
  // ends on the reading it asked for, not the one it replaced.
  let unsubscribe: (() => void) | null = null;
  if (opts.onHealthSettled !== undefined) {
    unsubscribe = opts.onHealthSettled(() => {
      if (!stillCurrent()) {
        unsubscribe?.();
        return;
      }
      void paintReconnect();
    });
  }

  // The empty state's words are a fact about the bank, so on a first load the
  // page waits for the shell's health reading instead of painting "Balances
  // appear after the first check" over a company with no bank and correcting
  // only when Refresh runs (rd r13 L3.7). The reconnect block is painted
  // first, so the wait still says it is checking rather than showing nothing.
  const reconnectPaint = paintReconnect();
  if (lastHealth() === null && opts.healthReadSettled !== undefined) {
    await opts.healthReadSettled();
  }
  await loadInner();
  await reconnectPaint;
}

function formatAmount(amount: string): string {
  const cents = decimalToCents(amount);
  if (cents === null) return amVisibleDisplayText(amount);
  return formatCents(cents);
}

/**
 * What kind of account this is, in the words the console uses. `depository` is
 * a bank's word for a bank account. "Wallet" is the decided word for a wallet
 * payment as a source (`rd-polish-rules` §1) — it is NOT the word for every
 * account that is not a deposit account, so a credit card is a credit card and
 * a loan is a loan. A Moov wallet arrives typed `depository` with a wallet
 * name, and that is the only signal the snapshot carries.
 */
export function accountKindText(account: { type: string; name: string }): string {
  if (/wallet|moov/i.test(account.name)) return "Wallet";
  const type = account.type.trim().toLowerCase();
  if (type === "depository") return "Bank account";
  if (type === "credit") return "Credit card";
  if (type === "loan") return "Loan";
  if (type === "investment") return "Investment";
  return "Account";
}

function accountCaption(account: BalanceAccount, maskDisplay: string): string {
  const parts = [accountKindText(account)];
  if (account.mask.trim() !== "") parts.push(maskDisplay);
  parts.push(amVisibleDisplayText(account.currency));
  if (account.asOf.trim() !== "") {
    const when = account.asOf.includes("T")
      ? timeAgoText(account.asOf)
      : formatAsOf(account.asOf);
    if (when !== "") parts.push(`last checked ${when}`);
  }
  return parts.join(" · ");
}

/**
 * Plain note only when this bank row and one books cash balance can both be
 * read. Wallet accounts (a Moov segment) are not the bank figure.
 */
export function bankBooksDifferenceNote(
  account: BalanceAccount,
  books: readonly LedgerAccount[] | null,
  depositoryCount: number,
): string | null {
  if (books === null) return null;
  if (account.type !== "depository" || account.currency !== "USD") return null;
  const bankCents = decimalToCents(account.currentBalance);
  if (bankCents === null) return null;
  // The match is on the ONE account label, so what the person reads on the row
  // is what this reconciliation compared: an account whose label names the Moov
  // wallet is not the bank figure, and the bank's own account name matches the
  // label's last words.
  const cash = books.filter((row) => row.isCash && !leafOf(row.account).join(" ").includes("Moov"));
  const name = account.name.trim().toLowerCase();
  const named = cash.filter((row) => leafOf(row.account).at(-1)?.toLowerCase() === name);
  let match: LedgerAccount | undefined;
  if (named.length === 1) match = named[0];
  else if (named.length === 0 && depositoryCount === 1 && cash.length === 1) match = cash[0];
  if (match === undefined) return null;
  const booksCents = decimalToCents(match.closing);
  if (booksCents === null) return null;
  const gap = bankCents > booksCents ? bankCents - booksCents : booksCents - bankCents;
  if (gap === 0n) return null;
  return `Books differ from the bank by ${formatCents(gap)} (not reconciled yet)`;
}

/**
 * The words of one account label, last segment last (the account's own name).
 * Used to COMPARE a books cash account with a bank's own account name, never
 * printed: the answer is a sentence about figures, so no company names are
 * needed.
 */
function leafOf(path: Parameters<typeof accountFace>[0]): string[] {
  return accountFace(path, NO_COMPANY_NAMES).split(" › ");
}

function booksNote(
  account: BalanceAccount,
  books: readonly LedgerAccount[] | null,
  depositoryCount: number,
): HTMLElement | null {
  const text = bankBooksDifferenceNote(account, books, depositoryCount);
  if (text === null) return null;
  return h("p", { class: "books-diff", "data-role": "books-diff" }, [text]);
}

async function loadBooksCash(
  client: AdminClient,
  company: CompanySlug,
): Promise<readonly LedgerAccount[] | null> {
  try {
    const response = await client.getLedgerAccounts(company, defaultPeriod());
    return response.accounts;
  } catch {
    return null;
  }
}
